import React, { useState, useEffect, useCallback } from 'react';

const C = {
 accent:'#3AAF58', accentLight:'#EAF7EE', accentDark:'#2E8B47',
 bg:'#F0F4F0', surface:'#FFFFFF',
 border:'#E2E8E2', border2:'#CDD8CD',
 text:'#1A2E1E', text2:'#3D5E44', text3:'#7A9A80',
 amber:'#D4840F', amberBg:'#FEF6E7', amberBorder:'#F0D499',
 red:'#C83333', redBg:'#FDEAEA', redBorder:'#F0B8B8',
 blue:'#FFFFFF', blueBg:'#EAF1FB',
 purple:'#6B48A8', purpleBg:'#F0EBFB',
 teal:'#1A7A7A', tealBg:'#E7F5F5',
 dark:'#0F1E14', dark2:'#1A3A22',
};
const inr = n => '₹'+Number(Math.round(n)||0).toLocaleString('en-IN');
const pct = n => Math.round(n||0)+'%';
const API = `${process.env.REACT_APP_API_URL || 'https://api.botanix.in'}/api/reports`;

// ── FETCH HOOK ────────────────────────────────────────────────────────────────
function useReport(endpoint, params={}) {
 const [data, setData] = useState(null);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState(null);

 const load = useCallback(async () => {
 setLoading(true); setError(null);
 try {
 const query = new URLSearchParams(params).toString();
 const res = await fetch(`${API}${endpoint}${query?'?'+query:''}`);
 if(!res.ok) throw new Error(`HTTP ${res.status}`);
 setData(await res.json());
 } catch(err) { setError(err.message); }
 finally { setLoading(false); }
 }, [endpoint, JSON.stringify(params)]);

 useEffect(()=>{ load(); }, [load]);
 return { data, loading, error, refresh:load };
}

// ── ATOMS ─────────────────────────────────────────────────────────────────────
const Card = ({children,style={}}) => (
 <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:14,boxShadow:'0 2px 8px rgba(0,0,0,.05)',...style}}>{children}</div>
);

const SectionHeader = ({title,icon,color,bg,right}) => (
 <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'14px 20px',borderBottom:`1px solid ${C.border}`,background:bg||'#F5F8F5',borderRadius:'14px 14px 0 0'}}>
 <div style={{display:'flex',alignItems:'center',gap:8}}>
 <span style={{fontSize:20}}>{icon}</span>
 <span style={{fontFamily:'Plus Jakarta Sans,sans-serif',fontSize:15,fontWeight:800,color:color||C.text}}>{title}</span>
 </div>
 {right}
 </div>
);

const KPI = ({label,value,sub,icon,color,bg}) => (
 <div style={{padding:'16px',background:C.surface,borderRadius:14,border:`1px solid ${C.border}`,boxShadow:'0 2px 6px rgba(0,0,0,.04)'}}>
 <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:10}}>
 <div style={{fontSize:10,fontWeight:700,color:C.text3,textTransform:'uppercase',letterSpacing:'.07em'}}>{label}</div>
 <div style={{width:34,height:34,borderRadius:10,background:bg||C.accentLight,display:'flex',alignItems:'center',justifyContent:'center',fontSize:18}}>{icon}</div>
 </div>
 <div style={{fontFamily:'Plus Jakarta Sans,sans-serif',fontSize:24,fontWeight:800,color:color||C.text,letterSpacing:'-.02em',marginBottom:3}}>{value}</div>
 {sub&&<div style={{fontSize:11,color:C.text3}}>{sub}</div>}
 </div>
);

const Bar = ({label,value,max,color,format}) => {
 const w = Math.round((value/Math.max(max,1))*100);
 return (
 <div style={{marginBottom:12}}>
 <div style={{display:'flex',justifyContent:'space-between',fontSize:12,marginBottom:5}}>
 <span style={{fontWeight:600,color:C.text}}>{label}</span>
 <span style={{fontWeight:700,color:color||C.accent}}>{format?format(value):value}</span>
 </div>
 <div style={{height:7,background:C.border,borderRadius:99}}>
 <div style={{height:7,width:`${w}%`,background:color||C.accent,borderRadius:99,transition:'width .5s'}}/>
 </div>
 </div>
 );
};

const Spinner = () => (
 <div style={{textAlign:'center',padding:'40px',color:C.text3}}>
 <div style={{fontSize:24,marginBottom:8}}></div>
 <div style={{fontSize:13}}>Loading from database...</div>
 </div>
);

const ErrorMsg = ({msg}) => (
 <div style={{padding:'16px',background:C.redBg,borderRadius:10,border:`1px solid ${C.redBorder}`,fontSize:12,color:C.red,margin:16}}>
 {msg} — Check backend is running on port 5000
 </div>
);

// ── DATE RANGE PICKER ─────────────────────────────────────────────────────────
const today = new Date().toISOString().slice(0,10);
const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0,10);
const yearStart = new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0,10);

const PRESETS = [
 {label:'This Month', from:monthStart, to:today},
 {label:'This Year', from:yearStart, to:today},
 {label:'Last 30 Days', from:new Date(Date.now()-30*86400000).toISOString().slice(0,10), to:today},
 {label:'Last 90 Days', from:new Date(Date.now()-90*86400000).toISOString().slice(0,10), to:today},
];

function DateRangePicker({from, to, onChange}) {
 return (
 <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
 {PRESETS.map(p=>(
 <button key={p.label} onClick={()=>onChange(p.from,p.to)}
 style={{padding:'5px 12px',borderRadius:99,border:`1px solid ${from===p.from&&to===p.to?C.accent:C.border2}`,background:from===p.from&&to===p.to?C.accentLight:'transparent',color:from===p.from&&to===p.to?C.accent:C.text3,fontSize:11,fontWeight:600,cursor:'pointer'}}>
 {p.label}
 </button>
 ))}
 <div style={{display:'flex',gap:6,alignItems:'center'}}>
 <input type="date" value={from} onChange={e=>onChange(e.target.value,to)}
 style={{padding:'5px 10px',borderRadius:8,border:`1px solid ${C.border2}`,fontSize:12,color:C.text,background:C.bg}}/>
 <span style={{fontSize:11,color:C.text3}}>to</span>
 <input type="date" value={to} onChange={e=>onChange(from,e.target.value)}
 style={{padding:'5px 10px',borderRadius:8,border:`1px solid ${C.border2}`,fontSize:12,color:C.text,background:C.bg}}/>
 </div>
 </div>
 );
}

// ── REVENUE REPORT ────────────────────────────────────────────────────────────
function RevenueReport() {
 const [from,setFrom] = useState(monthStart);
 const [to, setTo] = useState(today);
 const { data, loading, error } = useReport('/revenue', {from,to});

 const maxRev = data ? Math.max(...(data.monthly||[]).map(m=>parseFloat(m.revenue||0)),1) : 1;

 return (
 <div>
 <div style={{marginBottom:20}}><DateRangePicker from={from} to={to} onChange={(f,t)=>{setFrom(f);setTo(t);}}/></div>

 {loading&&<Spinner/>}
 {error&&<ErrorMsg msg={error}/>}
 {data&&!loading&&(
 <>
 <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:14,marginBottom:20}}>
 <KPI label="Total Revenue" value={inr(data.totalRevenue)} icon="" color={C.accent}/>
 <KPI label="Transactions" value={data.monthly.reduce((s,m)=>s+parseInt(m.transactions||0),0)} icon="" color={C.blue} bg={C.blueBg}/>
 <KPI label="Monthly Average" value={inr(data.totalRevenue/Math.max(data.monthly.length,1))} icon="" color={C.purple} bg={C.purpleBg}/>
 </div>

 <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16}}>
 {/* Monthly trend */}
 <Card>
 <SectionHeader title="Monthly Revenue Trend" icon="" color={C.accent} bg={C.accentLight}/>
 <div style={{padding:'16px 20px'}}>
 {data.monthly.map((m,i)=>(
 <Bar key={i} label={m.month} value={parseFloat(m.revenue||0)} max={maxRev} color={C.accent} format={inr}/>
 ))}
 {data.monthly.length===0&&<div style={{color:C.text3,fontSize:13,textAlign:'center',padding:'20px'}}>No revenue data for this period</div>}
 </div>
 </Card>

 {/* Payment modes */}
 <Card>
 <SectionHeader title="Revenue by Payment Mode" icon="" color={C.blue} bg={C.blueBg}/>
 <div style={{padding:'16px 20px'}}>
 {data.paymentModes.map((m,i)=>{
 const colors=[C.blue,C.accent,C.purple,C.amber,C.teal];
 return <Bar key={i} label={m.payment_mode||'Unknown'} value={parseFloat(m.total||0)} max={data.totalRevenue||1} color={colors[i%colors.length]} format={inr}/>;
 })}
 {data.paymentModes.length===0&&<div style={{color:C.text3,fontSize:13,textAlign:'center',padding:'20px'}}>No payment data</div>}
 </div>
 </Card>
 </div>

 {/* Invoice types */}
 <Card>
 <SectionHeader title="Revenue by Invoice Type" icon="" color={C.purple} bg={C.purpleBg}/>
 <div style={{padding:'16px 20px',display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10}}>
 {data.invoiceTypes.map((t,i)=>{
 const colors=[C.accent,C.blue,C.purple,C.amber];
 return (
 <div key={i} style={{padding:'14px',background:C.bg,borderRadius:12,textAlign:'center'}}>
 <div style={{fontSize:11,color:C.text3,textTransform:'uppercase',marginBottom:6}}>{t.invoice_type}</div>
 <div style={{fontFamily:'Plus Jakarta Sans,sans-serif',fontSize:18,fontWeight:800,color:colors[i%colors.length]}}>{inr(t.total)}</div>
 <div style={{fontSize:10,color:C.text3,marginTop:3}}>{t.invoice_count} invoices</div>
 </div>
 );
 })}
 </div>
 </Card>
 </>
 )}
 </div>
 );
}

// ── BOOKINGS REPORT ───────────────────────────────────────────────────────────
function BookingsReport() {
 const [from,setFrom] = useState(yearStart);
 const [to, setTo] = useState(today);
 const { data, loading, error } = useReport('/bookings', {from,to});

 return (
 <div>
 <div style={{marginBottom:20}}><DateRangePicker from={from} to={to} onChange={(f,t)=>{setFrom(f);setTo(t);}}/></div>
 {loading&&<Spinner/>}
 {error&&<ErrorMsg msg={error}/>}
 {data&&!loading&&(
 <>
 <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:14,marginBottom:20}}>
 <KPI label="Total Bookings" value={data.summary.total} icon="" color={C.blue} bg={C.blueBg}/>
 <KPI label="Confirmed" value={data.summary.confirmed} icon="" color={C.accent}/>
 <KPI label="Total Value" value={inr(data.summary.total_value)} icon="" color={C.accent}/>
 <KPI label="Outstanding" value={inr(data.summary.outstanding)} icon="" color={C.amber} bg={C.amberBg}/>
 </div>

 <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16}}>
 <Card>
 <SectionHeader title="Bookings by Type" icon="" color={C.blue} bg={C.blueBg}/>
 <div style={{padding:'16px 20px'}}>
 {data.byType.map((t,i)=>(
 <Bar key={i} label={t.booking_type} value={parseInt(t.count)} max={Math.max(...data.byType.map(x=>parseInt(x.count)))} color={[C.blue,C.accent,C.purple,C.amber,C.teal][i%5]}/>
 ))}
 </div>
 </Card>
 <Card>
 <SectionHeader title="Bookings by Source" icon="" color={C.purple} bg={C.purpleBg}/>
 <div style={{padding:'16px 20px'}}>
 {data.bySource.map((s,i)=>(
 <Bar key={i} label={s.source||'Direct'} value={parseInt(s.count)} max={Math.max(...data.bySource.map(x=>parseInt(x.count)))} color={[C.purple,C.accent,C.blue,C.amber][i%4]}/>
 ))}
 </div>
 </Card>
 </div>

 {/* Recent bookings table */}
 <Card>
 <SectionHeader title="Recent Bookings" icon="" color={C.text}/>
 <div style={{overflowX:'auto'}}>
 <table style={{width:'100%',borderCollapse:'collapse'}}>
 <thead><tr style={{background:'#F5F8F5'}}>
 {['Booking ID','Guest/Company','Type','Date','Pax','Amount','Status'].map(h=>(
 <th key={h} style={{padding:'10px 14px',fontSize:11,fontWeight:700,color:C.text3,textAlign:'left',textTransform:'uppercase',letterSpacing:'.06em',borderBottom:`1px solid ${C.border}`}}>{h}</th>
 ))}
 </tr></thead>
 <tbody>
 {data.recent.map((b,i)=>(
 <tr key={i} style={{borderBottom:`1px solid ${C.border}`,background:i%2?'#FAFCFA':C.surface}}>
 <td style={{padding:'10px 14px',fontSize:12,fontWeight:600,color:C.blue}}>{b.booking_code}</td>
 <td style={{padding:'10px 14px',fontSize:12}}>{b.guest_name||b.company_name||'—'}</td>
 <td style={{padding:'10px 14px',fontSize:12}}>{b.booking_type}</td>
 <td style={{padding:'10px 14px',fontSize:12,color:C.text3}}>{b.start_date}</td>
 <td style={{padding:'10px 14px',fontSize:12}}>{b.pax_count}</td>
 <td style={{padding:'10px 14px',fontSize:12,fontWeight:700,color:C.accent}}>{inr(b.total_amount)}</td>
 <td style={{padding:'10px 14px'}}>
 <span style={{padding:'2px 10px',borderRadius:99,fontSize:11,fontWeight:600,
 color:b.booking_status==='Confirmed'?C.accent:b.booking_status==='Cancelled'?C.red:C.amber,
 background:b.booking_status==='Confirmed'?C.accentLight:b.booking_status==='Cancelled'?C.redBg:C.amberBg}}>
 {b.booking_status}
 </span>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 </Card>
 </>
 )}
 </div>
 );
}

// ── HR REPORT ─────────────────────────────────────────────────────────────────
function HRReport() {
 const [from,setFrom] = useState(monthStart);
 const [to, setTo] = useState(today);
 const { data, loading, error } = useReport('/hr', {from,to});

 return (
 <div>
 <div style={{marginBottom:20}}><DateRangePicker from={from} to={to} onChange={(f,t)=>{setFrom(f);setTo(t);}}/></div>
 {loading&&<Spinner/>}
 {error&&<ErrorMsg msg={error}/>}
 {data&&!loading&&(
 <>
 <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:14,marginBottom:20}}>
 <KPI label="Total Staff" value={data.payroll.total_staff} icon="" color={C.purple} bg={C.purpleBg}/>
 <KPI label="Monthly Payroll" value={inr(data.payroll.total_payroll)} icon="" color={C.accent}/>
 <KPI label="Avg Salary" value={inr(data.payroll.avg_salary)} icon="" color={C.blue} bg={C.blueBg}/>
 <KPI label="Attendance Days" value={data.attendance.days_tracked||0} icon="" color={C.teal} bg={C.tealBg}/>
 </div>

 <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
 <Card>
 <SectionHeader title="Staff by Department" icon="" color={C.purple} bg={C.purpleBg}/>
 <div style={{padding:'16px 20px'}}>
 {data.departments.map((d,i)=>(
 <Bar key={i} label={d.department} value={parseInt(d.headcount)} max={Math.max(...data.departments.map(x=>parseInt(x.headcount)))} color={[C.purple,C.accent,C.blue,C.amber,C.teal][i%5]}/>
 ))}
 </div>
 </Card>
 <Card>
 <SectionHeader title="Attendance Summary" icon="" color={C.accent} bg={C.accentLight}/>
 <div style={{padding:'16px 20px'}}>
 {[
 ['Present', data.attendance.present||0, C.accent],
 ['Absent', data.attendance.absent||0, C.red],
 ['Late', data.attendance.late||0, C.amber],
 ['On Leave', data.attendance.on_leave||0, C.blue],
 ].map(([l,v,c])=>{
 const total = parseInt(data.attendance.present||0)+parseInt(data.attendance.absent||0)+parseInt(data.attendance.late||0)+parseInt(data.attendance.on_leave||0);
 return <Bar key={l} label={l} value={parseInt(v)} max={Math.max(total,1)} color={c}/>;
 })}
 </div>
 </Card>
 </div>
 </>
 )}
 </div>
 );
}

// ── GST REPORT ────────────────────────────────────────────────────────────────
function GSTReport() {
 const [from,setFrom] = useState(monthStart);
 const [to, setTo] = useState(today);
 const { data, loading, error } = useReport('/gst', {from,to});

 return (
 <div>
 <div style={{marginBottom:20}}><DateRangePicker from={from} to={to} onChange={(f,t)=>{setFrom(f);setTo(t);}}/></div>
 {loading&&<Spinner/>}
 {error&&<ErrorMsg msg={error}/>}
 {data&&!loading&&(
 <>
 <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:14,marginBottom:20}}>
 {[['Taxable',inr(data.totals.taxable),C.text],['CGST',inr(data.totals.cgst),C.blue],['SGST',inr(data.totals.sgst),C.purple],['IGST',inr(data.totals.igst),C.teal],['Total GST',inr(data.totals.cgst+data.totals.sgst+data.totals.igst),C.accent]].map(([l,v,c])=>(
 <KPI key={l} label={l} value={v} icon="" color={c}/>
 ))}
 </div>
 <Card>
 <SectionHeader title="GST Invoice Register" icon="" color={C.text}
 right={<span style={{fontSize:11,color:C.text3}}>{data.invoices.length} invoices</span>}/>
 <div style={{overflowX:'auto'}}>
 <table style={{width:'100%',borderCollapse:'collapse'}}>
 <thead><tr style={{background:'#F5F8F5'}}>
 {['Invoice No','Date','Type','Taxable','CGST','SGST','IGST','Total','Status'].map(h=>(
 <th key={h} style={{padding:'9px 12px',fontSize:10,fontWeight:700,color:C.text3,textAlign:'left',textTransform:'uppercase',borderBottom:`1px solid ${C.border}`}}>{h}</th>
 ))}
 </tr></thead>
 <tbody>
 {data.invoices.map((inv,i)=>(
 <tr key={i} style={{borderBottom:`1px solid ${C.border}`,background:i%2?'#FAFCFA':C.surface}}>
 <td style={{padding:'9px 12px',fontSize:12,fontWeight:600,color:C.blue}}>{inv.invoice_number}</td>
 <td style={{padding:'9px 12px',fontSize:11,color:C.text3}}>{inv.invoice_date?.slice(0,10)}</td>
 <td style={{padding:'9px 12px',fontSize:11}}>{inv.invoice_type}</td>
 <td style={{padding:'9px 12px',fontSize:12}}>{inr(inv.taxable_amount)}</td>
 <td style={{padding:'9px 12px',fontSize:12}}>{inr(inv.cgst)}</td>
 <td style={{padding:'9px 12px',fontSize:12}}>{inr(inv.sgst)}</td>
 <td style={{padding:'9px 12px',fontSize:12}}>{inr(inv.igst)}</td>
 <td style={{padding:'9px 12px',fontSize:12,fontWeight:700,color:C.accent}}>{inr(inv.total_amount)}</td>
 <td style={{padding:'9px 12px'}}>
 <span style={{padding:'2px 8px',borderRadius:99,fontSize:10,fontWeight:600,
 color:inv.payment_status==='paid'?C.accent:inv.payment_status==='partial'?C.amber:C.red,
 background:inv.payment_status==='paid'?C.accentLight:inv.payment_status==='partial'?C.amberBg:C.redBg}}>
 {inv.payment_status}
 </span>
 </td>
 </tr>
 ))}
 {data.invoices.length===0&&(
 <tr><td colSpan={9} style={{padding:'24px',textAlign:'center',color:C.text3}}>No invoices for this period</td></tr>
 )}
 </tbody>
 </table>
 </div>
 </Card>
 </>
 )}
 </div>
 );
}

// ── MAIN REPORTS MODULE ───────────────────────────────────────────────────────
const TABS = [
 {id:'summary', label:'Summary', },
 {id:'revenue', label:'Revenue', },
 {id:'bookings', label:'Bookings', },
 {id:'occupancy', label:'Occupancy', },
 {id:'hr', label:'HR & Payroll', },
 {id:'gst', label:'GST Register', },
];

export default function Reports() {
 const [tab, setTab] = useState('summary');
 const { data:summary, loading:sl } = useReport('/summary');

 return (
 <div style={{fontFamily:'Inter,sans-serif',color:C.text}}>
 {/* Header */}
 <div style={{background:`linear-gradient(135deg,${C.dark},${C.dark2})`,padding:'24px 28px',position:'sticky',top:0,zIndex:50}}>
 <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:20}}>
 <div>
 <div style={{fontSize:10,color:'rgba(255,255,255,.3)',textTransform:'uppercase',letterSpacing:'.14em',marginBottom:6}}> Analytics & Business Intelligence</div>
 <div style={{fontFamily:'Plus Jakarta Sans,sans-serif',fontSize:26,fontWeight:800,color:'#fff',letterSpacing:'-.02em'}}>Reports & Analytics</div>
 <div style={{fontSize:12,color:'rgba(255,255,255,.4)',marginTop:2}}>Live data from PostgreSQL · {new Date().toLocaleDateString('en-IN',{day:'numeric',month:'long',year:'numeric'})}</div>
 </div>
 <button onClick={()=>window.print()} style={{padding:'9px 18px',borderRadius:10,border:'1px solid rgba(255,255,255,.2)',background:'rgba(255,255,255,.1)',color:'#fff',fontSize:12,fontWeight:600,cursor:'pointer'}}>
 Print Report
 </button>
 </div>

 {/* Summary KPIs in header */}
 {summary&&(
 <div style={{display:'grid',gridTemplateColumns:'repeat(6,1fr)',gap:10}}>
 {[
 {l:'Revenue Today', v:inr(summary.revenue?.today||0), ic:'₹', c:C.accent},
 {l:'This Month', v:inr(summary.revenue?.this_month||0), ic:'', c:'#81C784'},
 {l:'This Year', v:inr(summary.revenue?.this_year||0), ic:'', c:'#64B5F6'},
 {l:'Total Bookings', v:summary.bookings?.total||0, ic:'', c:'#CE93D8'},
 {l:'Occupancy', v:pct(parseInt(summary.rooms?.occupied||0)/Math.max(parseInt(summary.rooms?.total||12),1)*100), ic:'',c:'#80CBC4'},
 {l:'Active Staff', v:summary.staff?.total||0, ic:'', c:'#FFB347'},
 ].map(k=>(
 <div key={k.l} style={{padding:'12px',background:'rgba(255,255,255,.07)',borderRadius:12,border:'1px solid rgba(255,255,255,.08)',textAlign:'center'}}>
 <div style={{fontSize:18,marginBottom:4}}>{k.ic}</div>
 <div style={{fontFamily:'Plus Jakarta Sans,sans-serif',fontSize:18,fontWeight:800,color:k.c,marginBottom:2}}>{k.v}</div>
 <div style={{fontSize:9,color:'rgba(255,255,255,.3)',textTransform:'uppercase',letterSpacing:'.05em'}}>{k.l}</div>
 </div>
 ))}
 </div>
 )}
 {sl&&<div style={{textAlign:'center',padding:'16px',color:'rgba(255,255,255,.3)',fontSize:12}}>Loading live data...</div>}
 </div>

 {/* Tabs */}
 <div style={{background:C.surface,borderBottom:`1px solid ${C.border}`,padding:'0 28px',display:'flex',gap:0,overflowX:'auto'}}>
 {TABS.map(t=>(
 <button key={t.id} onClick={()=>setTab(t.id)}
 style={{padding:'12px 20px',background:'transparent',border:'none',cursor:'pointer',fontSize:13,fontWeight:600,color:tab===t.id?C.accent:C.text3,borderBottom:`3px solid ${tab===t.id?C.accent:'transparent'}`,display:'flex',alignItems:'center',gap:6,whiteSpace:'nowrap',transition:'all .15s'}}>
 {t.icon} {t.label}
 </button>
 ))}
 </div>

 {/* Content */}
 <div style={{padding:'24px 28px'}}>
 {tab==='summary'&&(
 <div>
 {summary&&(
 <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:16}}>
 <Card>
 <SectionHeader title="Revenue Summary" icon="" color={C.accent} bg={C.accentLight}/>
 <div style={{padding:'16px 20px'}}>
 {[['Today',inr(summary.revenue?.today||0),C.accent],['This Month',inr(summary.revenue?.this_month||0),C.blue],['This Year',inr(summary.revenue?.this_year||0),C.purple]].map(([l,v,c])=>(
 <div key={l} style={{display:'flex',justifyContent:'space-between',padding:'10px 0',borderBottom:`1px solid ${C.border}`}}>
 <span style={{fontSize:13,color:C.text3}}>{l}</span>
 <span style={{fontSize:14,fontWeight:800,color:c}}>{v}</span>
 </div>
 ))}
 </div>
 </Card>
 <Card>
 <SectionHeader title="Bookings Summary" icon="" color={C.blue} bg={C.blueBg}/>
 <div style={{padding:'16px 20px'}}>
 {[['Total Bookings',summary.bookings?.total||0,C.text],['Confirmed',summary.bookings?.confirmed||0,C.accent],['Today',summary.bookings?.today||0,C.blue]].map(([l,v,c])=>(
 <div key={l} style={{display:'flex',justifyContent:'space-between',padding:'10px 0',borderBottom:`1px solid ${C.border}`}}>
 <span style={{fontSize:13,color:C.text3}}>{l}</span>
 <span style={{fontFamily:'Plus Jakarta Sans,sans-serif',fontSize:20,fontWeight:800,color:c}}>{v}</span>
 </div>
 ))}
 </div>
 </Card>
 <Card>
 <SectionHeader title="Operations Summary" icon="" color={C.purple} bg={C.purpleBg}/>
 <div style={{padding:'16px 20px'}}>
 {[
 ['Room Occupancy',pct(parseInt(summary.rooms?.occupied||0)/Math.max(parseInt(summary.rooms?.total||12),1)*100),C.accent],
 ['Total Staff',summary.staff?.total||0,C.purple],
 ['Low Stock Items',summary.inventory?.low_stock||0,C.amber],
 ].map(([l,v,c])=>(
 <div key={l} style={{display:'flex',justifyContent:'space-between',padding:'10px 0',borderBottom:`1px solid ${C.border}`}}>
 <span style={{fontSize:13,color:C.text3}}>{l}</span>
 <span style={{fontFamily:'Plus Jakarta Sans,sans-serif',fontSize:20,fontWeight:800,color:c}}>{v}</span>
 </div>
 ))}
 </div>
 </Card>
 </div>
 )}
 {sl&&<Spinner/>}
 </div>
 )}
 {tab==='revenue'&&<RevenueReport/>}
 {tab==='bookings'&&<BookingsReport/>}
 {tab==='occupancy'&&<div style={{padding:'40px',textAlign:'center',color:C.text3}}><div style={{fontSize:48,marginBottom:16}}></div><div style={{fontSize:16,fontWeight:700,marginBottom:8}}>Occupancy Report</div><div style={{fontSize:13}}>Coming soon — connecting room reservation data</div></div>}
 {tab==='hr'&&<HRReport/>}
 {tab==='gst'&&<GSTReport/>}
 </div>
 </div>
 );
}
